import { getTemplateByName } from 'client/store/modules/models/ApplicationModel';
import ApplicationApi from 'client/api/v2/ApplicationApi';
import PasswordManagerApi from 'client/api/v2/PasswordManagerApi';
import Vue from 'vue';

/**
 * Under the hood, Password Manager is an SSO Application.
 * Activating the password manager means creating an
 * application from the template 'jcpwm'
 * and configure Password Manager SCIM server.
 * This Module methods dispatch ApplicationModel actions
 * to create the Application.
 */
const JC_PWM_CATALOG_NAME = 'jcpwm';

const SET_IS_ENABLED = 'SET_IS_ENABLED';
const SET_ACCESS_LEVELS = 'SET_ACCESS_LEVELS';
const SET_APPLICATION_ID = 'SET_APPLICATION_ID';

export const initialState = () => ({
  isEnabled: false,
  applicationId: '',
  accessLevels: [],
  accessLevelsLoaded: false,
});

const loadPasswordManagerApplicationTemplate = async (dispatch) => {
  const template = await getTemplateByName(JC_PWM_CATALOG_NAME);

  dispatch('ApplicationModel/resetItem', null, { root: true });
  dispatch('ApplicationModel/setTemplateId', template._id, { root: true });
  dispatch('ApplicationModel/setItemStateToCreate', null, { root: true });
  await dispatch('ApplicationModel/fetchItem', null, { root: true });
  dispatch('ApplicationModel/setItemField',
    { key: 'ssoUrl', value: template.ssoUrl }, { root: true });
};

const createPasswordManagerApplication = async (dispatch) => {
  dispatch('ApplicationModel/setItemField',
    { key: 'displayLabel', value: 'JC Password Manager' }, { root: true });

  dispatch('ApplicationModel/setItemField',
    { key: 'active', value: true }, { root: true });

  dispatch('ApplicationModel/unset',
    { key: 'config' }, { root: true });

  // Create Application
  return dispatch(
    'ApplicationModel/saveItem',
    null,
    { root: true },
  );
};

/**
 * this function will create a company record in Password Manager Database
 * @returns {Promise<{ apiKey: string  }>}
 */
const createPasswordManagerRecord = async (applicationId) => {
  try {
    const passwordManagerStatusResponse = await PasswordManagerApi.activatePasswordManager();

    if (!passwordManagerStatusResponse.data.apiKey) {
      throw new Error();
    }

    return passwordManagerStatusResponse.data;
  } catch (error) {
    // rollback created application in JC DB
    ApplicationApi.delete(applicationId);
    throw new Error('failed to enable Password Manager');
  }
};

const deleteApplication = (applicationId) => Promise.all([
  ApplicationApi.delete(applicationId),
  PasswordManagerApi.deactivatePasswordManager(),
]);

const activatePasswordManagerScim = async (dispatch, applicationId, apiKey) => {
  try {
    await dispatch('ApplicationModel/configureScim', {}, { root: true });
    dispatch('ApplicationModel/setScimCredentials', {
      keyValue: apiKey,
    }, { root: true });
    await dispatch('ApplicationModel/activateScim', {}, { root: true });
  } catch (error) {
    // rollback created application in JC DB and PWM DB
    deleteApplication(applicationId);
    throw new Error('failed to confgure SCIM');
  }
};

const resetApplicationState = (dispatch) => {
  dispatch('ApplicationModel/resetState', null, { root: true });
  dispatch('ApplicationModel/resetItem', null, { root: true });
};

/**
 * @returns {Promise<{ enabled:boolean, apiKey: string  }>}
 */
const getPasswordManager = async () => {
  try {
    const resp = await PasswordManagerApi.getPasswordManager();
    return {
      enabled: true,
      applicationId: resp.data.applicationId,
    };
  } catch (error) {
    if (error?.response?.status === 404) {
      return {
        enabled: false,
        applicationId: '',
      };
    }
    throw error;
  }
};

export default {
  namespaced: true,
  state: initialState(),

  actions: {
    async fetchAccessLevels({ commit }) {
      const resp = await PasswordManagerApi.fetchAccessLevels();
      commit(SET_ACCESS_LEVELS, resp.data.results);
      return resp;
    },

    async fetchPasswordManagerStatus({ commit }) {
      const resp = await getPasswordManager();
      commit(SET_IS_ENABLED, resp.enabled);
      commit(SET_APPLICATION_ID, resp.applicationId);
      return resp;
    },

    async activatePasswordManager({ commit, dispatch }) {
      try {
        await loadPasswordManagerApplicationTemplate(dispatch);
        const application = await createPasswordManagerApplication(dispatch);

        const { apiKey } = await createPasswordManagerRecord(application.id);
        await activatePasswordManagerScim(dispatch, application.id, apiKey);

        // Update Store State
        commit(SET_IS_ENABLED, true);
        commit(SET_APPLICATION_ID, application.id);
      } finally {
        resetApplicationState(dispatch);
      }
    },

    fetchPasswordManagerStatistics() {
      return PasswordManagerApi.fetchStatistics();
    },

    async removePasswordManager({ dispatch, state }) {
      await deleteApplication(state.applicationId);
      dispatch('clearPasswordManager');
    },

    clearPasswordManager({ commit }) {
      const passwordManagerInitialState = initialState();
      commit(SET_IS_ENABLED, passwordManagerInitialState.isEnabled);
      commit(SET_APPLICATION_ID, passwordManagerInitialState.applicationId);
    },

  },

  getters: {
    accessLevels(state) {
      return state.accessLevels;
    },

    accessLevelsLoaded(state) {
      return state.accessLevelsLoaded;
    },

    getPasswordManagerStatus(state) {
      return state.isEnabled;
    },

    getPasswordManagerApplicationId(state) {
      return state.applicationId;
    },
  },

  mutations: {
    [SET_IS_ENABLED](state, isEnabled) {
      Vue.set(state, 'isEnabled', isEnabled);
    },
    [SET_ACCESS_LEVELS](state, accessLevels) {
      Vue.set(state, 'accessLevels', accessLevels);
      Vue.set(state, 'accessLevelsLoaded', true);
    },
    [SET_APPLICATION_ID](state, applicationId) {
      Vue.set(state, 'applicationId', applicationId);
    },
  },
};
